import { faSort, IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortDown, faSortUp } from '@fortawesome/sharp-solid-svg-icons';
import { CheckboxProps } from '@kcftech/design-system';
import { ToggleSelectionCheckbox } from 'pages/Desk/issues/issue_modals/styled';
import { memo, ReactNode, useMemo } from 'react';
import type { CSSProp } from 'styled-components';
import styled from 'styled-components';
import { primaryText, secondaryBackgroundColor, useThemeValues } from 'styles/theme';
import { ExpandedOrderByConfig } from 'utils/query_v3';

const Layout = styled.div<{
    $gridTemplateColumns: string;
    $layoutStyles?: CSSProp;
}>`
    display: grid;
    grid-template-rows: auto;
    grid-template-columns: ${({ $gridTemplateColumns }) => $gridTemplateColumns};
    padding: 0.75rem 1.5rem;
    align-items: center;
    justify-content: space-between;
    font-size: 1.4rem;
    text-decoration: none;
    box-sizing: border-box;
    border-radius: 8px;
    color: ${primaryText};
    background-color: ${secondaryBackgroundColor};
    ${({ $layoutStyles }) => $layoutStyles}
`;

const SortItem = styled.div<{
    $isSortable: boolean;
    $sortItemStyles?: CSSProp;
}>`
    margin: 0rem 1rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    cursor: ${({ $isSortable }) => ($isSortable ? 'pointer' : 'default')};
    ${({ $sortItemStyles }) => $sortItemStyles}
`;

export const DefaultSortBarItemGridSize = '1fr';

/**
 * Generates the grid template columns for the sort bar configuration.
 */
export function getSortBarGridTemplateColumns(
    itemsConfig: SortBarItemsConfig[],
    selectAllEnabled = false
) {
    const itemColumns = itemsConfig
        .map((config) => config.gridTemplateColumn ?? DefaultSortBarItemGridSize)
        .join(' ');

    return selectAllEnabled ? `auto ${itemColumns}` : itemColumns;
}

type SortIcon = Readonly<{
    /**
     * Icon to show in the sort bar
     */
    icon: IconDefinition;
    /**
     * Color of the sort icon
     */
    color: string;
}>;

export type SortBarItemsConfig = Readonly<{
    /**
     * Unique identifier for the column.
     */
    id: string | number;
    /**
     * Primary content of the sort item (heading)
     */
    title: ReactNode;
    /**
     * Optional field to sort on.
     * If not supplied, the sort icon is not rendered
     */
    field?: string;
    /**
     * Space the column will take up in the grid (defaults to: `'1fr'`)
     */
    gridTemplateColumn?: string;
}>;

export type SortBarSelectAllConfig = Pick<
    CheckboxProps,
    'checked' | 'indeterminate' | 'onChange'
>;

export type SortBarProps = Readonly<{
    id: string;
    /**
     * Header items to display
     */
    itemsConfig: SortBarItemsConfig[];
    /**
     * Optional config for a select all checkbox
     */
    selectAllConfig?: SortBarSelectAllConfig;
    /**
     * Current sort applied
     */
    sort: ExpandedOrderByConfig[];
    /**
     * Callback to change the sort applied
     */
    onSort: (sort: ExpandedOrderByConfig | undefined) => void;
    /**
     * Optional style overrides for the Layout component
     */
    layoutStyles?: CSSProp;
    /**
     * Optional style overrides for the SortItem component
     */
    sortItemStyles?: CSSProp;
}>;

function SortBar({
    id,
    itemsConfig,
    selectAllConfig,
    sort,
    onSort,
    layoutStyles,
    sortItemStyles,
}: SortBarProps) {
    const { primaryText: primaryTextColor, disabledText } = useThemeValues();

    const gridTemplateColumns = useMemo(
        () => getSortBarGridTemplateColumns(itemsConfig, selectAllConfig !== undefined),
        [itemsConfig, selectAllConfig]
    );

    const isSortApplied = (fieldToCheck: string) =>
        sort.find(({ field }) => field === fieldToCheck);

    const getSortIcon = (field: string): SortIcon => {
        const sortApplied = isSortApplied(field);
        return sortApplied
            ? {
                  icon: sortApplied.dir === 'asc' ? faSortUp : faSortDown,
                  color: primaryTextColor,
              }
            : { icon: faSort, color: disabledText };
    };

    const onSortField = (field: string) => {
        const sortApplied = isSortApplied(field);
        return sortApplied
            ? onSort(sortApplied.dir === 'asc' ? { field, dir: 'desc' } : undefined)
            : onSort({ field, dir: 'asc' });
    };

    return (
        <Layout
            id={id}
            $gridTemplateColumns={gridTemplateColumns}
            $layoutStyles={layoutStyles}
        >
            {selectAllConfig && <ToggleSelectionCheckbox {...selectAllConfig} label="" />}
            {itemsConfig.map((item) => (
                <SortItem
                    id={`${id}-sort-item-${item.id}`}
                    key={item.id}
                    $isSortable={item.field !== undefined}
                    $sortItemStyles={sortItemStyles}
                    onClick={() => (item.field ? onSortField(item.field) : undefined)}
                >
                    {item.title}
                    {item.field && (
                        <FontAwesomeIcon {...getSortIcon(item.field)} className="f-10" />
                    )}
                </SortItem>
            ))}
        </Layout>
    );
}

const memoSortBar = memo(SortBar);
export { memoSortBar as SortBar };
